import { action, ActionType } from "typesafe-actions";

import { ApprovalStatus } from "../../../types/approval-status/approval-status.type";
import { FileAttachment } from "../../../features/file-attachments/types/file-attachment.interface";
import { IError } from "../../../redux/actions/errorHandlingAction";
import { Nullable } from "../../../types/common";

import { CampaignBriefForm } from "../types/campaign-brief-form/campaign-brief-form.interface";
import { CampaignBriefProductsActionType } from "../features/campaign-brief-products/store/campaign-brief-products.actions";
import { CreateCampaignBriefForm } from "../types/create-campaign-brief-form.interface";
import { DuplicateCampaignBriefForm } from "../types/duplicate-campaign-brief-form.interface";
import { ICampaignBriefData } from "../types/campaign-brief-state/campaign-brief-state.interface";

export enum CampaignBriefActions {
  SetBrief = "CampaignBrief/SetBrief",
  SetBriefStatus = "CampaignBrief/SetBriefStatus",

  CloseNewBriefModal = "CampaignBrief/CloseNewBriefModal",
  OpenNewBriefModal = "CampaignBrief/OpenNewBriefModal",

  Create = "CampaignBrief/Create",
  CreateSuccess = "CampaignBrief/CreateSuccess",
  CreateFailure = "CampaignBrief/CreateFailure",

  Save = "CampaignBrief/Save",

  Rename = "CampaignBrief/Rename",
  RenameFailure = "CampaignBrief/RenameFailure",
  RenameSuccess = "CampaignBrief/RenameSuccess",

  Fetch = "CampaignBrief/Fetch",
  FetchSuccess = "CampaignBrief/FetchSuccess",
  FetchFailure = "CampaignBrief/FetchFailure",

  Update = "CampaignBrief/Update",
  UpdateSuccess = "CampaignBrief/UpdateSuccess",
  UpdateFailure = "CampaignBrief/UpdateFailure",

  ResetBrief = "CampaignBrief/ResetBrief",
  ResetSuccess = "CampaignBrief/ResetSuccess",
  ResetFailure = "CampaignBrief/ResetFailure",

  Search = "CampaignBrief/Search",
  SearchSuccess = "CampaignBrief/SearchSuccess",
  SearchFailure = "CampaignBrief/SearchFailure",
  ClearSearchResults = "CampaignBrief/ClearSearchResults",

  StartReview = "CampaignBrief/StartReview",
  StartReviewSuccess = "CampaignBrief/StartReviewSuccess",
  StartReviewFailure = "CampaignBrief/StartReviewFailure",

  ResetState = "CampaignBrief/ResetState",
  ResetAttribute = "CampaignBrief/ResetAttribute",
  ResetErrors = "CampaignBrief/ResetErrors",

  Duplicate = "CampaignBrief/Duplicate",
  DuplicateSuccess = "CampaignBrief/DuplicateSuccess",
  DuplicateFailure = "CampaignBrief/DuplicateError",

  FetchAttachedFiles = "CampaignBrief/FetchAttachedFiles",
  FetchAttachedFilesSuccess = "CampaignBrief/FetchAttachedFilesSuccess",
  FetchAttachedFilesFailure = "CampaignBrief/FetchAttachedFilesFailure",

  SetAttachedFiles = "CampaignBrief/SetAttachedFiles",

  RemoveAttachedFile = "CampaignBrief/RemoveAttachedFile",
  RemoveAttachedFileSuccess = "CampaignBrief/RemoveAttachedFileSuccess",
  RemoveAttachedFileFailure = "CampaignBrief/RemoveAttachedFileFailure",

  ToggleShowAddProductsModal = "CampaignBrief/ToggleShowAddProductsModal",

  UploadAttachmentFile = "CampaignBrief/UploadAttachmentFile",
  UploadAttachmentFileSuccess = "CampaignBrief/UploadAttachmentFileSuccess",
  UploadAttachmentFileFailure = "CampaignBrief/UploadAttachmentFileFailure",
}

export const closeNewBriefModal = () => action(CampaignBriefActions.CloseNewBriefModal);
export const openNewBriefModal = () => action(CampaignBriefActions.OpenNewBriefModal);

export const setBrief = (brief: ICampaignBriefData) => action(CampaignBriefActions.SetBrief, brief);

export const create = (form: CreateCampaignBriefForm) => action(CampaignBriefActions.Create, form);
export const createSuccess = (brief: ICampaignBriefData) =>
  action(CampaignBriefActions.CreateSuccess, brief);
export const createFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.CreateFailure, error);

export const save = (form: CampaignBriefForm) => action(CampaignBriefActions.Save, form);
export const fetch = (id: number) => action(CampaignBriefActions.Fetch, id);
export const fetchSuccess = (brief: ICampaignBriefData) =>
  action(CampaignBriefActions.FetchSuccess, brief);
export const fetchFailure = (error: IError) => action(CampaignBriefActions.FetchFailure, error);

export const update = (form: CampaignBriefForm) => action(CampaignBriefActions.Update, form);
export const updateSuccess = (result: ICampaignBriefData, form: CampaignBriefForm) =>
  action(CampaignBriefActions.UpdateSuccess, { result, form });
export const updateFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.UpdateFailure, error);

export const resetBrief = (form: CampaignBriefForm) =>
  action(CampaignBriefActions.ResetBrief, form);

export const resetSuccess = (result: any, form: CampaignBriefForm) =>
  action(CampaignBriefActions.ResetSuccess, { result, form });
export const resetFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.ResetFailure, error);

export const rename = (form: CampaignBriefForm) => action(CampaignBriefActions.Rename, form);
export const renameFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.RenameFailure, error);
export const renameSuccess = (result: ICampaignBriefData) =>
  action(CampaignBriefActions.RenameSuccess, result);

export const search = (term: string) => action(CampaignBriefActions.Search, term);
export const searchSuccess = (results: any[]) =>
  action(CampaignBriefActions.SearchSuccess, { results });
export const searchFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.SearchFailure, error);
export const clearSearchResults = () => action(CampaignBriefActions.ClearSearchResults);

export const setBriefStatus = (status: ApprovalStatus) =>
  action(CampaignBriefActions.SetBriefStatus, status);

export const startReview = (briefId: number | string) =>
  action(CampaignBriefActions.StartReview, briefId);
export const startReviewSuccess = () => action(CampaignBriefActions.StartReviewSuccess);
export const startReviewFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.StartReviewFailure, error);

export const resetState = () => action(CampaignBriefActions.ResetState);
export const resetCampaignAttributeText = (attributeName: string) =>
  action(CampaignBriefActions.ResetAttribute, attributeName);
export const resetErrors = () => action(CampaignBriefActions.ResetErrors);

export const duplicate = (form: DuplicateCampaignBriefForm) =>
  action(CampaignBriefActions.Duplicate, form);
export const duplicateSuccess = (result: ICampaignBriefData) =>
  action(CampaignBriefActions.DuplicateSuccess, result);
export const duplicateFailure = (error: Nullable<IError>) =>
  action(CampaignBriefActions.DuplicateFailure, error);

export const fetchAttachedFiles = () => action(CampaignBriefActions.FetchAttachedFiles);
export const fetchAttachedFilesSuccess = (files: FileAttachment[]) =>
  action(CampaignBriefActions.FetchAttachedFilesSuccess, files);
export const fetchAttachedFilesFailure = (error: IError) =>
  action(CampaignBriefActions.FetchAttachedFilesFailure, error);

export const setAttachedFiles = (files: FileAttachment[]) =>
  action(CampaignBriefActions.SetAttachedFiles, files);

export const removeAttachedFile = (fileId: number) =>
  action(CampaignBriefActions.RemoveAttachedFile, fileId);
export const removeAttachedFileSuccess = (attachments: FileAttachment[]) =>
  action(CampaignBriefActions.RemoveAttachedFileSuccess, attachments);
export const removeAttachedFileFailure = (error: IError) =>
  action(CampaignBriefActions.RemoveAttachedFileFailure, error);

export const toggleShowAddProductsModal = (show: boolean) =>
  action(CampaignBriefActions.ToggleShowAddProductsModal, show);

export const uploadAttachedFile = (briefId: string, file: File) =>
  action(CampaignBriefActions.UploadAttachmentFile, { briefId, file });
export const uploadAttachedFileSuccess = (file: any) =>
  action(CampaignBriefActions.UploadAttachmentFileSuccess, file);
export const uploadAttachedFileFailure = (error: IError) =>
  action(CampaignBriefActions.UploadAttachmentFileFailure, error);

const actionCreators = {
  closeNewBriefModal,
  openNewBriefModal,
  create,
  setBrief,
  createSuccess,
  createFailure,
  save,
  fetch,
  fetchSuccess,
  fetchFailure,
  update,
  updateSuccess,
  updateFailure,
  resetBrief,
  resetSuccess,
  resetFailure,
  search,
  searchSuccess,
  searchFailure,
  clearSearchResults,
  setBriefStatus,
  startReview,
  startReviewSuccess,
  startReviewFailure,
  resetState,
  resetCampaignAttributeText,
  resetErrors,
  removeAttachedFile,
  removeAttachedFileSuccess,
  removeAttachedFileFailure,
  duplicate,
  duplicateSuccess,
  duplicateFailure,
  fetchAttachedFiles,
  fetchAttachedFilesSuccess,
  fetchAttachedFilesFailure,
  setAttachedFiles,
  toggleShowAddProductsModal,
  uploadAttachedFile,
  uploadAttachedFileSuccess,
  uploadAttachedFileFailure,
  rename,
  renameFailure,
  renameSuccess,
};

export type CampaignBriefActionType =
  | ActionType<typeof actionCreators>
  | CampaignBriefProductsActionType;
